import React, { useContext, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  TextField,
  MenuItem,
  Stack,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { getStatisticsTotalsRowSx, viewRootTextPrimarySx } from '../../util/chartTheme';
import Grid2 from '@mui/material/Unstable_Grid2/Grid2';
import dayjs from 'dayjs';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx-js-style';
import AuthContext from '../../context/authContext';
import useVoucher from '../../hooks/useVoucher';
import useResourceVoucher from '../../hooks/useResourceVoucher';

function formatEuro(euros) {
  return (
    Number(euros || 0).toLocaleString('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + ' €'
  );
}

const VoucherMonthlySummary = () => {
  const theme = useTheme();
  const authCtx = useContext(AuthContext);
  const totalsRowSx = useMemo(() => getStatisticsTotalsRowSx(theme), [theme]);

  if (!authCtx.checkRight('accounting:special:tools')) {
    return <Navigate to='/welcome' replace />;
  }

  const { getVoucherMonthlyReport } = useVoucher();
  const { getResourceVoucherMonthlyRedemptionReport } = useResourceVoucher();

  const initialMonth = dayjs().subtract(1, 'month');
  const [year, setYear] = useState(initialMonth.year());
  const [month, setMonth] = useState(initialMonth.month() + 1);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [resourceVoucherResults, setResourceVoucherResults] = useState([]);

  const yearOptions = useMemo(() => {
    const currentYear = dayjs().year();
    const years = [];
    for (let y = currentYear - 5; y <= currentYear + 1; y += 1) {
      years.push(y);
    }
    return years;
  }, []);

  const handleLoad = async () => {
    setLoading(true);
    try {
      const [voucherData, rvData] = await Promise.all([
        getVoucherMonthlyReport(year, month),
        getResourceVoucherMonthlyRedemptionReport(year, month),
      ]);
      setResults(voucherData?.results || []);
      setResourceVoucherResults(rvData?.results || []);
    } catch (error) {
      // always display error toast in german
      toast.error('Fehler beim Laden der Gutschein-Monatssummen');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const summaryRows = useMemo(() => {
    const map = new Map();

    (results || []).forEach(item => {
      const key = String(item.branchId || 'unknown');
      if (!map.has(key)) {
        map.set(key, {
          branchId: item.branchId || null,
          branchName: item.branchName || 'Unbekannt',
          byType: {},
        });
      }
      const row = map.get(key);
      const typeKey = item.type || 'unknown';
      const current = row.byType[typeKey] || { normal: 0, gifted: 0, total: 0 };
      const normal = Number(item.totalNormalBalance || 0);
      const gifted = Number(item.totalGiftedBalance || 0);
      current.normal += normal;
      current.gifted += gifted;
      current.total += normal + gifted;
      row.byType[typeKey] = current;
    });

    const rows = Array.from(map.values()).map(row => {
      const redeemRaw = row.byType.redeem || { normal: 0, gifted: 0, total: 0 };
      const loadRaw = row.byType.load || { normal: 0, gifted: 0, total: 0 };

      const redeem = {
        normal: -redeemRaw.normal,
        gifted: -redeemRaw.gifted,
        total: -redeemRaw.total,
      };

      const load = {
        normal: loadRaw.normal,
        gifted: loadRaw.gifted,
        total: loadRaw.total,
      };

      const saldo = load.total + redeem.total;

      return {
        branchName: row.branchName,
        redeem,
        load,
        saldo,
      };
    });

    rows.sort((a, b) => a.branchName.localeCompare(b.branchName));

    return rows;
  }, [results]);

  const grandTotals = useMemo(() => {
    return summaryRows.reduce(
      (acc, row) => {
        acc.redeem.normal += row.redeem.normal;
        acc.redeem.gifted += row.redeem.gifted;
        acc.redeem.total += row.redeem.total;
        acc.load.normal += row.load.normal;
        acc.load.gifted += row.load.gifted;
        acc.load.total += row.load.total;
        acc.saldo += row.saldo;
        return acc;
      },
      {
        redeem: { normal: 0, gifted: 0, total: 0 },
        load: { normal: 0, gifted: 0, total: 0 },
        saldo: 0,
      }
    );
  }, [summaryRows]);

  const resourceVoucherRows = useMemo(() => {
    const rows = (resourceVoucherResults || []).map(item => {
      return {
        branchName: item.branchName || 'Unbekannt',
        redeemedSum: Number(item.totalRedeemedSum || 0),
        redeemedCount: Number(item.redeemedCount || 0),
      };
    });
    rows.sort((a, b) => a.branchName.localeCompare(b.branchName));
    return rows;
  }, [resourceVoucherResults]);

  const resourceVoucherTotals = useMemo(() => {
    return resourceVoucherRows.reduce(
      (acc, row) => {
        acc.redeemedSum += row.redeemedSum;
        acc.redeemedCount += row.redeemedCount;
        return acc;
      },
      { redeemedSum: 0, redeemedCount: 0 }
    );
  }, [resourceVoucherRows]);

  const handleExportXlsx = () => {
    if (!summaryRows.length && !resourceVoucherRows.length) {
      toast.info('Keine Daten zum Exportieren verfügbar');
      return;
    }

    const aoa = [];
    const monthLabel = `${String(month).padStart(2, '0')}.${year}`;

    const titleValueRow = aoa.length;
    aoa.push(['Gutschein Monatssumme (Wertgutscheine)']);
    const monthValueRow = aoa.length;
    aoa.push(['Monat', monthLabel]);
    aoa.push([]);

    const headerValueRow = aoa.length;
    aoa.push([
      'Filiale',
      'Einlösungen normal',
      'Einlösungen geschenkt',
      'Einlösungen gesamt',
      'Aufladungen normal',
      'Aufladungen geschenkt',
      'Aufladungen gesamt',
      'Saldo gesamt',
    ]);

    const firstValueDataRow = aoa.length;
    summaryRows.forEach(row => {
      aoa.push([
        row.branchName,
        row.redeem.normal,
        row.redeem.gifted,
        row.redeem.total,
        row.load.normal,
        row.load.gifted,
        row.load.total,
        row.saldo,
      ]);
    });

    aoa.push([]);
    const totalValueRow = aoa.length;
    aoa.push([
      'Gesamt',
      grandTotals.redeem.normal,
      grandTotals.redeem.gifted,
      grandTotals.redeem.total,
      grandTotals.load.normal,
      grandTotals.load.gifted,
      grandTotals.load.total,
      grandTotals.saldo,
    ]);

    aoa.push([]);
    const titleResourceRow = aoa.length;
    aoa.push(['Produktgutschein Einlösungen']);
    const monthResourceRow = aoa.length;
    aoa.push(['Monat', monthLabel]);
    aoa.push([]);
    const headerResourceRow = aoa.length;
    aoa.push(['Filiale', 'Einlösungen gesamt', 'Anzahl Gutscheine']);

    const firstResourceDataRow = aoa.length;
    resourceVoucherRows.forEach(row => {
      aoa.push([row.branchName, row.redeemedSum, row.redeemedCount]);
    });

    aoa.push([]);
    const totalResourceRow = aoa.length;
    aoa.push(['Gesamt', resourceVoucherTotals.redeemedSum, resourceVoucherTotals.redeemedCount]);

    const worksheet = XLSX.utils.aoa_to_sheet(aoa);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Gutschein Monat');

    const boldHeaderRows = [
      titleValueRow,
      monthValueRow,
      headerValueRow,
      totalValueRow,
      titleResourceRow,
      monthResourceRow,
      headerResourceRow,
      totalResourceRow,
    ];

    const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1:A1');

    boldHeaderRows.forEach(rowIndex => {
      for (let col = range.s.c; col <= range.e.c; col += 1) {
        const cellAddress = XLSX.utils.encode_cell({ r: rowIndex, c: col });
        const cell = worksheet[cellAddress];
        if (!cell) continue;
        cell.s = {
          ...(cell.s || {}),
          font: {
            ...(cell.s && cell.s.font ? cell.s.font : {}),
            bold: true,
          },
        };
      }
    });

    const euroFormat = '#,##0.00 [$€-407]';

    // Value vouchers section: format euro columns (B..H) for data and total rows
    if (summaryRows.length > 0) {
      for (let r = firstValueDataRow; r <= totalValueRow; r += 1) {
        for (let c = 1; c <= 7; c += 1) {
          const cellAddress = XLSX.utils.encode_cell({ r, c });
          const cell = worksheet[cellAddress];
          if (!cell || typeof cell.v !== 'number') continue;
          cell.t = 'n';
          cell.z = euroFormat;
          cell.s = {
            ...(cell.s || {}),
            numFmt: euroFormat,
          };
        }
      }
    }

    // Resource vouchers section: format euro column (B) for data and total rows
    if (resourceVoucherRows.length > 0) {
      for (let r = firstResourceDataRow; r <= totalResourceRow; r += 1) {
        const cellAddress = XLSX.utils.encode_cell({ r, c: 1 });
        const cell = worksheet[cellAddress];
        if (!cell || typeof cell.v !== 'number') continue;
        cell.t = 'n';
        cell.z = euroFormat;
        cell.s = {
          ...(cell.s || {}),
          numFmt: euroFormat,
        };
      }
    }

    // make branch names (Filiale) bold in all data and total rows
    if (summaryRows.length > 0) {
      for (let r = firstValueDataRow; r <= totalValueRow; r += 1) {
        const cellAddress = XLSX.utils.encode_cell({ r, c: 0 });
        const cell = worksheet[cellAddress];
        if (!cell) continue;
        cell.s = {
          ...(cell.s || {}),
          font: {
            ...(cell.s && cell.s.font ? cell.s.font : {}),
            bold: true,
          },
        };
      }
    }
    if (resourceVoucherRows.length > 0) {
      for (let r = firstResourceDataRow; r <= totalResourceRow; r += 1) {
        const cellAddress = XLSX.utils.encode_cell({ r, c: 0 });
        const cell = worksheet[cellAddress];
        if (!cell) continue;
        cell.s = {
          ...(cell.s || {}),
          font: {
            ...(cell.s && cell.s.font ? cell.s.font : {}),
            bold: true,
          },
        };
      }
    }

    // auto-size columns based on content width
    const colWidths = [];
    for (let c = range.s.c; c <= range.e.c; c += 1) {
      let maxLen = 0;
      for (let r = range.s.r; r <= range.e.r; r += 1) {
        const cellAddress = XLSX.utils.encode_cell({ r, c });
        const cell = worksheet[cellAddress];
        if (!cell || cell.v === undefined || cell.v === null) continue;
        const cellText = String(cell.v);
        if (cellText.length > maxLen) {
          maxLen = cellText.length;
        }
      }
      colWidths[c] = { wch: maxLen + 2 };
    }
    worksheet['!cols'] = colWidths;

    const filename = `gutscheine_monatsauswertung_${year}-${String(month).padStart(2, '0')}.xlsx`;
    XLSX.writeFile(workbook, filename);
  };

  const monthOptions = [
    { value: 1, label: 'Januar' },
    { value: 2, label: 'Februar' },
    { value: 3, label: 'März' },
    { value: 4, label: 'April' },
    { value: 5, label: 'Mai' },
    { value: 6, label: 'Juni' },
    { value: 7, label: 'Juli' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'Oktober' },
    { value: 11, label: 'November' },
    { value: 12, label: 'Dezember' },
  ];

  return (
    <Box
      sx={{
        ...viewRootTextPrimarySx,
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <Typography variant='h6' gutterBottom>
        Gutschein Monatssumme
      </Typography>

      <Paper sx={{ p: 2 }}>
        <Grid2 container spacing={2} alignItems='center'>
          <Grid2 xs={12} md={6}>
            <Stack direction='row' spacing={2}>
              <TextField
                select
                label='Jahr'
                value={year}
                onChange={e => setYear(Number(e.target.value) || year)}
                size='small'
                sx={{ minWidth: 120 }}
              >
                {yearOptions.map(y => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label='Monat'
                value={month}
                onChange={e => setMonth(Number(e.target.value) || month)}
                size='small'
                sx={{ minWidth: 160 }}
              >
                {monthOptions.map(m => (
                  <MenuItem key={m.value} value={m.value}>
                    {m.label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          </Grid2>
          <Grid2 xs={12} md={6}>
            <Box display='flex' justifyContent={{ xs: 'flex-start', md: 'flex-end' }} gap={2}>
              <Button
                variant='outlined'
                color='primary'
                onClick={handleExportXlsx}
                disabled={loading || (!summaryRows.length && !resourceVoucherRows.length)}
              >
                XLSX herunterladen
              </Button>
              <Button
                variant='contained'
                color='success'
                onClick={handleLoad}
                disabled={loading}
                sx={{ minWidth: 140 }}
              >
                {loading ? <CircularProgress size={20} color='inherit' /> : 'Auswerten'}
              </Button>
            </Box>
          </Grid2>
        </Grid2>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <TableContainer>
          <Table size='small' stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Filiale</TableCell>
                <TableCell align='right'>Einlösungen normal</TableCell>
                <TableCell align='right'>Einlösungen geschenkt</TableCell>
                <TableCell align='right'>Einlösungen gesamt</TableCell>
                <TableCell align='right'>Aufladungen normal</TableCell>
                <TableCell align='right'>Aufladungen geschenkt</TableCell>
                <TableCell align='right'>Aufladungen gesamt</TableCell>
                <TableCell align='right'>Saldo gesamt</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {summaryRows.map(row => (
                <TableRow key={row.branchName} hover>
                  <TableCell>{row.branchName}</TableCell>
                  <TableCell align='right'>{formatEuro(row.redeem.normal)}</TableCell>
                  <TableCell align='right'>{formatEuro(row.redeem.gifted)}</TableCell>
                  <TableCell align='right'>{formatEuro(row.redeem.total)}</TableCell>
                  <TableCell align='right'>{formatEuro(row.load.normal)}</TableCell>
                  <TableCell align='right'>{formatEuro(row.load.gifted)}</TableCell>
                  <TableCell align='right'>{formatEuro(row.load.total)}</TableCell>
                  <TableCell align='right'>{formatEuro(row.saldo)}</TableCell>
                </TableRow>
              ))}
              {!summaryRows.length && (
                <TableRow>
                  <TableCell colSpan={8} align='center'>
                    <Typography variant='body2' color='text.secondary'>
                      Keine Daten für den ausgewählten Monat.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {summaryRows.length > 0 && (
                <TableRow sx={totalsRowSx}>
                  <TableCell>
                    <strong>Gesamt</strong>
                  </TableCell>
                  <TableCell align='right'>
                    <strong>{formatEuro(grandTotals.redeem.normal)}</strong>
                  </TableCell>
                  <TableCell align='right'>
                    <strong>{formatEuro(grandTotals.redeem.gifted)}</strong>
                  </TableCell>
                  <TableCell align='right'>
                    <strong>{formatEuro(grandTotals.redeem.total)}</strong>
                  </TableCell>
                  <TableCell align='right'>
                    <strong>{formatEuro(grandTotals.load.normal)}</strong>
                  </TableCell>
                  <TableCell align='right'>
                    <strong>{formatEuro(grandTotals.load.gifted)}</strong>
                  </TableCell>
                  <TableCell align='right'>
                    <strong>{formatEuro(grandTotals.load.total)}</strong>
                  </TableCell>
                  <TableCell align='right'>
                    <strong>{formatEuro(grandTotals.saldo)}</strong>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant='subtitle1' gutterBottom>
          Produktgutschein Einlösungen
        </Typography>
        <TableContainer>
          <Table size='small' stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Filiale</TableCell>
                <TableCell align='right'>Einlösungen gesamt</TableCell>
                <TableCell align='right'>Anzahl Gutscheine</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {resourceVoucherRows.map(row => (
                <TableRow key={row.branchName} hover>
                  <TableCell>{row.branchName}</TableCell>
                  <TableCell align='right'>{formatEuro(row.redeemedSum)}</TableCell>
                  <TableCell align='right'>{row.redeemedCount}</TableCell>
                </TableRow>
              ))}
              {!resourceVoucherRows.length && (
                <TableRow>
                  <TableCell colSpan={3} align='center'>
                    <Typography variant='body2' color='text.secondary'>
                      Keine Daten für den ausgewählten Monat.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {resourceVoucherRows.length > 0 && (
                <TableRow sx={totalsRowSx}>
                  <TableCell>
                    <strong>Gesamt</strong>
                  </TableCell>
                  <TableCell align='right'>
                    <strong>{formatEuro(resourceVoucherTotals.redeemedSum)}</strong>
                  </TableCell>
                  <TableCell align='right'>
                    <strong>{resourceVoucherTotals.redeemedCount}</strong>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default VoucherMonthlySummary;
